import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Edit2,
  Trash2,
  Download,
  Building,
  User,
  FileText,
} from "lucide-react";
import { Formalite, TABLES, baserowAPI } from "../services/baserowAPI";
import { useFormalitesFilter } from "../hooks/useFormalitesFilter";
import { useCompanies } from "../hooks/useCompanies";
import { useClients } from "../hooks/useClients";
import Header from "./Header";
import { AddFormaliteModal } from "./AddFormaliteModal";
import { AddCompanyModal } from "./AddCompanyModal";
import { AddClientModal } from "./AddClientModal";
import EditCellModal from "./EditCellModal";
import FilterInput from "./FilterInput";
import { useDebounce } from "../hooks/useDebounce";

type SortDirection = "asc" | "desc" | null;
type SortConfig = {
  key: string | null;
  direction: SortDirection;
};

interface EditingCell {
  rowId: number;
  field: string;
  value: any;
  rowData: Formalite; // Données complètes de la ligne
}

interface FormaliteTableProps {
  targetRowId?: number | null;
  targetPage?: number | null;
  selectedDeadlineFilter?: {
    entreprise?: string;
    client?: string;
    type?: string;
  } | null;
  clearSelectedDeadlineFilter?: () => void;
  resetTarget?: () => void;
}

const FormaliteTable: React.FC<FormaliteTableProps> = ({ targetRowId, selectedDeadlineFilter, clearSelectedDeadlineFilter, resetTarget }) => {
  const AUTO_FILTER_MIN_LENGTH = 3;
  const PAGE_SIZE = 100; // Pagination côté client
  const [currentPage, setCurrentPage] = useState(1);

  // États pour le tri et les filtres
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'field_7264',
    direction: 'desc',
  });
  
  // État local pour les inputs de filtre (changements en temps réel)
  const [localFilters, setLocalFilters] = useState<Record<string, string>>({
    field_7261: "",
    field_7263: "",
    field_7260: "",
    field_7265: "",
  });

  // Filtres appliqués (après debounce)
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({
    field_7261: "",
    field_7263: "",
    field_7260: "",
    field_7265: "",
  });

  const debouncedLocalFilters = useDebounce(localFilters, 350);

  // Utiliser le hook de filtrage avec cache
  const {
    data,
    allData,
    loading,
    error,
    createRow,
    updateRow,
    deleteRow,
    refresh,
    totalCount,
    filteredCount,
  } = useFormalitesFilter(appliedFilters, sortConfig.key, sortConfig.direction);

  // Charger les entreprises et clients pour les mises à jour en temps réel
  const { companies } = useCompanies();
  const { clients } = useClients();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);

  // Callbacks pour les créations d'entreprises et clients
  const handleCompanyCreated = useCallback(() => {
    // Les données sont automatiquement à jour via le hook useCompanies
    setShowAddCompanyModal(false);
  }, []);

  const handleClientCreated = useCallback(() => {
    // Les données sont automatiquement à jour via le hook useClients
    setShowAddClientModal(false);
  }, []);

  // État pour l'édition avec modal
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);

  // Auto-remplir les filtres quand une échéance est sélectionnée
  useEffect(() => {
    if (!selectedDeadlineFilter) return;

    setCurrentPage(1); // Retour à la page 1
    setLocalFilters({
      field_7261: selectedDeadlineFilter.entreprise && selectedDeadlineFilter.entreprise !== 'Non défini'
        ? selectedDeadlineFilter.entreprise
        : "",
      field_7260: selectedDeadlineFilter.client && selectedDeadlineFilter.client !== 'Non défini'
        ? selectedDeadlineFilter.client
        : "",
      field_7263: selectedDeadlineFilter.type && selectedDeadlineFilter.type !== 'Non défini'
        ? selectedDeadlineFilter.type
        : "",
      field_7265: "",
    });
  }, [selectedDeadlineFilter]);

  // Appliquer les filtres côté client (auto avec debounce)
  useEffect(() => {
    const filterConfig: any = {};
    
    // Ne filtrer que si >= 3 caractères OU si c'est un filtre d'échéance
    const shouldApply = (value: string) => 
      value.trim().length >= AUTO_FILTER_MIN_LENGTH || selectedDeadlineFilter;

    if (debouncedLocalFilters.field_7261 && shouldApply(debouncedLocalFilters.field_7261)) {
      filterConfig.field_7261 = debouncedLocalFilters.field_7261;
    }
    if (debouncedLocalFilters.field_7260 && shouldApply(debouncedLocalFilters.field_7260)) {
      filterConfig.field_7260 = debouncedLocalFilters.field_7260;
    }
    if (debouncedLocalFilters.field_7263 && shouldApply(debouncedLocalFilters.field_7263)) {
      filterConfig.field_7263 = debouncedLocalFilters.field_7263;
    }
    if (debouncedLocalFilters.field_7265 && shouldApply(debouncedLocalFilters.field_7265)) {
      filterConfig.field_7265 = debouncedLocalFilters.field_7265;
    }

    setAppliedFilters(filterConfig);
    setCurrentPage(1); // Réinitialiser à la page 1 quand les filtres changent
  }, [debouncedLocalFilters, selectedDeadlineFilter]);

  // Pagination côté client - calculer les données à afficher
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage]);

  const totalPages = Math.ceil(filteredCount / PAGE_SIZE);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  // Fonctions de navigation
  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFirstPage = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLastPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(totalPages);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigation et scroll vers la ligne ciblée
  useEffect(() => {
    if (targetRowId && !loading) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(`row-${targetRowId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('bg-blue-100');
          element.style.transition = 'background-color 1s ease';
          
          setTimeout(() => {
            element.classList.remove('bg-blue-100');
            if (resetTarget) resetTarget();
          }, 3000);
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [targetRowId, loading, resetTarget]);

  // Formater une date au format français
  const formatDateFR = (dateStr: string | null): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Fonction de tri - côté client maintenant
  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }
    
    setSortConfig({ key: direction ? key : null, direction });
  };

  // Fonction de filtrage - met à jour l'état local
  const handleFilterChange = useCallback((key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Réinitialiser les filtres
  const handleClearFilters = () => {
    setLocalFilters({
      field_7261: "",
      field_7263: "",
      field_7260: "",
      field_7265: "",
    });
    if (clearSelectedDeadlineFilter) {
      clearSelectedDeadlineFilter();
    }
  };

  // Extraire une valeur de cellule
  const getCellValue = useCallback((item: Formalite, field: string): string => {
    const value = (item as any)[field];

    if (value === null || value === undefined) {
      return "";
    }

    // Formater les champs dates
    if (field === "field_7264" || field === "field_7270" || field === "field_7269" || field === "field_7271") {
      return formatDateFR(value as string);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "";
      if (typeof value[0] === "object" && "value" in value[0]) {
        return value.map((v: any) => v.value).join(", ");
      }
      return value.join(", ");
    }

    if (typeof value === "object" && "value" in value) {
      return (value as any).value;
    }

    return String(value);
  }, []);


  // Fonctions d'édition avec modal
  const startEdit = (rowId: number, field: string, currentValue: any, rowData: Formalite) => {
    // Champs non modifiables
    if (field === "field_7264" || field === "field_7273") {
      alert("Ce champ n'est pas modifiable");
      return;
    }
    setEditingCell({ rowId, field, value: currentValue, rowData });
  };

  const closeEditModal = () => {
    setEditingCell(null);
  };

  const saveEditModal = async (updateData: any) => {
    if (!editingCell) return;

    try {
      // updateData peut maintenant contenir plusieurs champs (champ modifié + échéances)
      await updateRow(editingCell.rowId, updateData);
      setEditingCell(null);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      alert("Erreur lors de la sauvegarde");
    }
  };

  // Fonction pour supprimer une ligne
  const handleDelete = async (rowId: number) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cette formalité ?")
    ) {
      await deleteRow(rowId);
    }
  };

  // Fonction pour ajouter une nouvelle formalité
  const handleAddFormalite = async (data: Partial<Formalite>) => {
    await createRow(data);
  };

  // Rendu des icônes de tri
  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    if (sortConfig.direction === "asc") {
      return <ChevronUp className="w-4 h-4 text-blue-600" />;
    }
    if (sortConfig.direction === "desc") {
      return <ChevronDown className="w-4 h-4 text-blue-600" />;
    }
    return <ChevronUp className="w-4 h-4 text-gray-400" />;
  };

  // Rendu d'une cellule
  const renderCell = (item: Formalite, field: string) => {
    const displayValue = getCellValue(item, field);
    const isNonEditable = field === "field_7264" || field === "field_7273";

    return (
      <div className="group flex items-center justify-between">
        <div
          className={`flex-1 px-2 py-1 rounded truncate ${
            isNonEditable
              ? "cursor-not-allowed text-gray-400"
              : "cursor-pointer hover:bg-gray-50"
          }`}
          onClick={() =>
            !isNonEditable && startEdit(item.id, field, (item as any)[field], item)
          }
          title={isNonEditable ? "Non modifiable" : displayValue}
        >
          <span className="text-sm">{displayValue || "-"}</span>
          {!isNonEditable && (
            <Edit2 className="inline-block w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2" />
          )}
        </div>
      </div>
    );
  };

  // Export en CSV
  const handleExportCSV = () => {
    if (data.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    const headers = [
      "Date création",
      "Entreprise",
      "Client",
      "Type",
      "Progression",
      "Échéance Auto",
      "Échéance Forcée",
      "Date envoi impôts",
      "Statut impôts",
      "Sender",
    ];

    const rows = data.map((item) => [
      getCellValue(item, "field_7264"),
      getCellValue(item, "field_7261"),
      getCellValue(item, "field_7260"),
      getCellValue(item, "field_7263"),
      getCellValue(item, "field_7265"),
      getCellValue(item, "field_7269"),
      getCellValue(item, "field_7270"),
      getCellValue(item, "field_7271"),
      getCellValue(item, "field_7272"),
      getCellValue(item, "field_7274"),
    ]);

    // Créer le CSV
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            // Échapper les guillemets et entourer les cellules contenant des virgules
            const escapedCell = String(cell || "").replace(/"/g, '""');
            return `"${escapedCell}"`;
          })
          .join(",")
      ),
    ].join("\n");

    // Créer un blob et télécharger
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `formalites_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 text-center">
          <p className="text-xl mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Formalités</h1>
          <p className="text-gray-600 text-sm">Gérez vos formalités administratives</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            title="Ajouter une formalité"
          >
            <FileText className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddCompanyModal(true)}
            className="p-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            title="Créer une nouvelle entreprise"
          >
            <Building className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddClientModal(true)}
            className="p-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
            title="Créer un nouveau client"
          >
            <User className="w-5 h-5" />
          </button>
          <button
            onClick={handleExportCSV}
            className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title="Exporter les données filtrées en CSV"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? "Chargement..." : "Actualiser"}
          </button>
        </div>
      </div>

      <AddFormaliteModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddFormalite}
      />

      <AddCompanyModal
        isOpen={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onCompanyCreated={handleCompanyCreated}
      />

      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onClientCreated={handleClientCreated}
      />

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* En-tête Date de création */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7264")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Date création
                    {renderSortIcon("field_7264")}
                  </button>
                </th>

                {/* En-tête Company */}
                <th className="px-3 py-3 text-left">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSort("field_7261")}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                    >
                      Entreprise
                      {renderSortIcon("field_7261")}
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <FilterInput
                      value={localFilters.field_7261}
                      onChange={(v) => handleFilterChange("field_7261", v)}
                      disabled={!!selectedDeadlineFilter}
                      placeholder="Filtrer..."
                    />
                  </div>
                </th>

                {/* En-tête Customer */}
                <th className="px-3 py-3 text-left">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSort("field_7260")}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                    >
                      Client
                      {renderSortIcon("field_7260")}
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <FilterInput
                      value={localFilters.field_7260}
                      onChange={(v) => handleFilterChange("field_7260", v)}
                      disabled={!!selectedDeadlineFilter}
                      placeholder="Filtrer..."
                    />
                  </div>
                </th>

                {/* En-tête Formality Type */}
                <th className="px-3 py-3 text-left">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSort("field_7263")}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                    >
                      Type
                      {renderSortIcon("field_7263")}
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <FilterInput
                      value={localFilters.field_7263}
                      onChange={(v) => handleFilterChange("field_7263", v)}
                      disabled={!!selectedDeadlineFilter}
                      placeholder="Filtrer..."
                    />
                  </div>
                </th>

                {/* En-tête Progression */}
                <th className="px-3 py-3 text-left">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSort("field_7265")}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                    >
                      Progression
                      {renderSortIcon("field_7265")}
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <FilterInput
                      value={localFilters.field_7265}
                      onChange={(v) => handleFilterChange("field_7265", v)}
                      disabled={!!selectedDeadlineFilter}
                      placeholder="Filtrer..."
                    />
                  </div>
                </th>

                {/* En-tête Description */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7262")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Description
                    {renderSortIcon("field_7262")}
                  </button>
                </th>

                {/* En-tête Regular Deadline */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7269")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Échéance Auto
                    {renderSortIcon("field_7269")}
                  </button>
                </th>

                {/* En-tête Forced Deadline */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7270")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Échéance Forcée
                    {renderSortIcon("field_7270")}
                  </button>
                </th>

                {/* En-tête Tax Deadline */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7271")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Date envoi impôts
                    {renderSortIcon("field_7271")}
                  </button>
                </th>

                {/* En-tête Tax Status */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7272")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Statut impôts
                    {renderSortIcon("field_7272")}
                  </button>
                </th>

                {/* En-tête Sender */}
                <th className="px-3 py-3 text-left">
                  <button
                    onClick={() => handleSort("field_7274")}
                    className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                  >
                    Sender
                    {renderSortIcon("field_7274")}
                  </button>
                </th>

                {/* En-tête Actions */}
                <th className="px-3 py-3 text-left">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </span>
                </th>
              </tr>
              <tr className="bg-blue-50 border-b-2 border-blue-200">
                <td colSpan={13} className="px-3 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    {selectedDeadlineFilter && (
                      <div className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                        Filtre actif (sélection dashboard)
                        {selectedDeadlineFilter.entreprise && selectedDeadlineFilter.entreprise !== 'Non défini' ? ` • ${selectedDeadlineFilter.entreprise}` : ''}
                        {selectedDeadlineFilter.client && selectedDeadlineFilter.client !== 'Non défini' ? ` • ${selectedDeadlineFilter.client}` : ''}
                        {selectedDeadlineFilter.type && selectedDeadlineFilter.type !== 'Non défini' ? ` • ${selectedDeadlineFilter.type}` : ''}
                      </div>
                    )}
                    {!selectedDeadlineFilter && (
                      <div className="text-xs text-gray-600">
                        Filtrage automatique dès 4 caractères saisis • {filteredCount} / {totalCount} formalités
                      </div>
                    )}
                    {selectedDeadlineFilter && (
                      <div className="text-xs text-gray-500">
                        {filteredCount} formalité{filteredCount> 1 ? 's' : ''} trouvée{filteredCount > 1 ? 's' : ''}
                      </div>
                    )}
                    <button
                      onClick={handleClearFilters}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-400 text-white text-sm font-medium rounded hover:bg-gray-500 disabled:bg-gray-300 transition"
                    >
                      Réinitialiser
                    </button>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((item, index) => (
                <tr
                  key={item.id}
                  id={`row-${item.id}`}
                  className={`group ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-blue-50 transition-colors duration-500`}
                >
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7264")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7261")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7260")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate" title={getCellValue(item, "field_7263")}>
                    {renderCell(item, "field_7263")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7265")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate" title={getCellValue(item, "field_7262")}>
                    {renderCell(item, "field_7262")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7269")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7270")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7271")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {renderCell(item, "field_7272")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate">
                    {renderCell(item, "field_7274")}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedData.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            Aucune donnée trouvée avec les filtres actuels
          </div>
        )}

        {filteredCount > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Informations */}
              <div className="text-sm text-gray-600">
                <p>
                  Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                  {' • '}
                  <span className="text-gray-500">
                    {filteredCount} formalité{filteredCount > 1 ? 's' : ''} 
                    {filteredCount < totalCount && ` (sur ${totalCount} au total)`}
                  </span>
                </p>
              </div>

              {/* Contrôles de pagination */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleFirstPage}
                  disabled={!hasPreviousPage || loading}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed rounded hover:bg-gray-200 transition"
                  title="Première page"
                >
                  <ChevronUp
                    className="w-5 h-5"
                    style={{ transform: "rotate(-90deg)" }}
                  />
                </button>

                <button
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage || loading}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed rounded hover:bg-gray-200 transition"
                  title="Page précédente"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded">
                  {currentPage} / {totalPages}
                </span>

                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage || loading}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed rounded hover:bg-gray-200 transition"
                  title="Page suivante"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                <button
                  onClick={handleLastPage}
                  disabled={!hasNextPage || loading}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed rounded hover:bg-gray-200 transition"
                  title="Dernière page"
                >
                  <ChevronDown
                    className="w-5 h-5"
                    style={{ transform: "rotate(-90deg)" }}
                  />
                </button>
              </div>

              {/* Badge Performance */}
              <div className="text-xs text-gray-500 px-3 py-1 bg-green-50 border border-green-200 rounded">
                ⚡ Filtrage instantané • {totalCount} formalités chargées
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'édition */}
      <EditCellModal
        isOpen={editingCell !== null}
        field={editingCell?.field || ""}
        value={editingCell?.value}
        currentRow={editingCell?.rowData}
        onSave={saveEditModal}
        onClose={closeEditModal}
      />
    </div>
  );
};

export default FormaliteTable;
